import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import React, { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import FileUploader from "./FileUploader";
import { Field, Form } from "react-final-form";
import { SpecViewer } from "./SpecViewer";
import ApiService from "../services/ApiService";
import CustomFileInput from "./CustomFileInput";
import Alert from "@material-ui/lab/Alert/Alert";
import { resolveAPIErrorMessage } from "modus/components/error-message/ErrorMessage";
import { useAsyncAction } from "legacy-components/form/action/AsyncAction";

interface SpecCreatorProps {
  apiId: string;
  version: string;
  onOpen: boolean;
  onClose: () => void;
  refreshDeployments: () => void;
}

export const SpecCreator: React.FC<SpecCreatorProps> = ({
  apiId,
  version,
  onOpen,
  onClose,
  refreshDeployments,
}) => {
  let submitData = new FormData();

  const [uploadFile, setUploadedFile] = React.useState<string>();
  const formRef = React.useRef<any>(null);

  const [state, { subscribe }] = useAsyncAction(() =>
    ApiService.uploadSpec(apiId, version, submitData)
  );

  const clear = () => {
    setUploadedFile(undefined);
    if (formRef.current) {
      formRef.current.change("specFile", undefined);
    }
  };

  const onSubmit = async (values: any) => {
    if (!values.specFile) {
      return;
    }
    submitData = new FormData();
    submitData.append("file", values.specFile[0]);
    subscribe();
  };

  useEffect(() => {
    if (!state.loaded || !onClose) {
      return;
    }
    if (state.loaded) {
      clear();
      onClose();
      refreshDeployments();
    }

 
  }, [state.loaded, onClose, refreshDeployments]);

  useEffect(() => {
    if(!refreshDeployments || !state.error) {
      return;
    }

    if (state.error  && !onOpen) {
      refreshDeployments();
    }
  }, [state.error, onOpen, refreshDeployments]);

  return (
    <Dialog
      open={onOpen}
      onClose={() => {
          onClose();  
          setTimeout(() => {
            clear();
          }, 100);
      }}
      title="Update the Specification File"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
         <Box display="flex" alignItems="center" justifyContent="space-between">
          <FormattedMessage defaultMessage="Update the Specification File" />
          <IconButton 
            aria-label="close"
            onClick={() => {
              onClose();
              setTimeout(() => {
            clear();
          }, 100);
            }}
            size="small"
            style={{ marginRight: -12, marginTop: -12 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent style={{ overflow: "hidden", paddingBottom: 0 }}>
        {!uploadFile && (
          <Box>
            <Box
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <FormattedMessage defaultMessage="Upload your modified Specification file" />
            </Box>
            <Box style={{ marginBottom: "16px" }}>
              <Form
                onSubmit={onSubmit}
                render={({ handleSubmit, submitting, pristine, form }) => {
                  formRef.current = form;
                  return (
                    <form id="spec-form" onSubmit={handleSubmit}>
                      <Box
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Field
                          name="specFile"
                          component={CustomFileInput}
                          accept={[".json", ".yaml", ".yml"]}
                          multiple={false}
                          boxContent={(props: any) => (
                            <FileUploader {...props} />
                          )}
                          onFileChange={(files: File[] | undefined) => {
                            if (files && files.length > 0) {
                              setUploadedFile(files[0].name);
                            } else {
                              setUploadedFile(undefined);
                            }
                          }}
                        />
                      </Box>
                    </form>
                  );
                }}
              />
            </Box>
          </Box>
        )}
        {uploadFile && (
          <Box>
            <Box marginBottom={"10px"}>
            {state.error && <Alert severity="error">{resolveAPIErrorMessage(state.errorValue)}</Alert>}
            </Box>
            <SpecViewer specName={uploadFile}></SpecViewer>
          </Box>
        )}
      </DialogContent>
      <DialogActions
        style={{ marginTop: "20px", marginBottom: "5px", marginRight: "20px" }}
      >
        <Button
          onClick={() => {
            onClose();
            setTimeout(() => {
            clear();
          }, 100);
          }}
          color="secondary"
          variant="outlined"
        >
          <FormattedMessage defaultMessage="Cancel" />
        </Button>
        <Button
          onClick={() => {
            const formState = formRef.current?.getState();
            if (formState?.values?.specFile) {
              submitData = new FormData(); // Reset FormDatasa
              submitData.append("file", formState.values.specFile[0]);
              subscribe();
            }
          }}
          color="primary"
          variant="contained"
          disabled={uploadFile === undefined}
        >
          <FormattedMessage defaultMessage="Upload" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
